import type { AptivadaAppFragment, BlockFragment } from "@ihr-radioedit/inferno-webapi";
import type { Store } from "@inferno/renderer-shared-core";
import { ContestEvent, hasPage, reverseRoute } from "@inferno/renderer-shared-core";
import classnames from "classnames";
import { inject } from "mobx-react";
import * as React from "react";
import { Translation } from "react-i18next";
import { Eyebrow } from "../../components/Eyebrow.component";
import { ShareButtons } from "../../components/integrations/ShareButtons.component";
import { abTranslator } from "../../lib/abHelper";
import { AptivadaProps } from "../../lib/aptivadaKeyword";
import { activateEmbeds } from "../../lib/embed";
import { getThemingCssFromTags } from "../../services/Sites.utils";
import { Container, Heading } from "../../ui";
import "../content/FrontMatter.style.scss";

interface ContestProps {
  contest: AptivadaAppFragment;
  block: BlockFragment;
  store?: Store;
}

interface ContestHeaderProps {
  eyebrowUrl: string;
  eyebrowText: string;
  title: string;
  frontMatter?: boolean;
}

declare global {
  interface Window {
    AptivadaAsyncInit: () => void;
    Aptivada: AptivadaProps;
  }
}

const contestTypeMap: { [key: string]: string } = {
  countdown: "ihrcountdown",
};

const ContestHeader = ({ eyebrowUrl, eyebrowText, title, frontMatter = false }: ContestHeaderProps) => {
  const headerCss = classnames("content-header", {
    "front-matter": frontMatter,
  });
  return (
    <header className={headerCss}>
      <Eyebrow topic_name={eyebrowText} url={eyebrowUrl} />
      <Heading level={1}>{title}</Heading>
    </header>
  );
};

@inject("store")
export class Contest extends React.Component<ContestProps> {
  private contentRef: React.RefObject<HTMLDivElement>;
  private sdkHost = "";

  constructor(props: ContestProps) {
    super(props);
    this.contentRef = React.createRef();
    this.storeBlock();

    console.log("props.contest", this.props.contest);

    if (props.contest.appType === "experience" && props.store?.env.APTIVADA_SDK_HOST_2) {
      this.sdkHost = props.store.env.APTIVADA_SDK_HOST_2;
    } else if (props.store?.env.APTIVADA_SDK_HOST) {
      this.sdkHost = props.store.env.APTIVADA_SDK_HOST;
    }
  }

  storeBlock() {
    if (this.props.store) {
      this.props.store.storeBlock({
        ...this.props.block,
        resolved: { ...this.props.contest }, // This is intentional, we want to duplicate the object.
      });
    }
  }

  initSdk = () => {
    const { contest } = this.props;
    const campaignType = contestTypeMap[contest.appType] || contest.appType;
    window.AptivadaAsyncInit = () => {
      window.Aptivada.init({
        campaignId: parseInt(contest.appId, 10),
        campaignType,
        host: this.sdkHost,
        transparent: true,
        mobileRedirect: false,
        initialHeight: 2600,
        events: {
          userLogout: () => {
            this.logout();
          },
          userSubmitKeyword: this.handleUserCompleteEntry,
        },
      });
    };
    // Moving this out of componentDidMount
    // and putting it in a timeout.
    // It was loading contests twice with the previous config. - SR
    setTimeout(() => {
      if (this.contentRef.current) {
        activateEmbeds(this.contentRef.current);
      }
    }, 100);
  };

  private logout = () => {
    if (this.props.store) {
      this.props.store.session.currentSession?.logout();
    }
  };

  private handleUserCompleteEntry = () => {
    const { store, contest } = this.props;
    const campaignType = contestTypeMap[contest.appType] || contest.appType;

    if (store && store.page.currentPage) {
      store.onContestAction.dispatch({
        sectionName: "",
        pageName: store.page.currentPage.name,
        context: campaignType,
        action: "contest_exit",
        name: contest.title,
        id: parseInt(contest.appId, 10) || 0,
        exitType: "entry_success",
      } as ContestEvent);
    }

    this.contentRef.current?.scrollIntoView({ block: "start", behavior: "smooth", inline: "nearest" });
  };

  componentDidMount() {
    // Only initialize SDK for non-experience (Audience 1.0) contests
    // Audience 2.0 contests auto-initialize via data attributes
    if (this.isExperience()) {
      return;
    }

    if (typeof document !== "undefined" && document.readyState === "complete") {
      this.initSdk();
      return;
    }
    if (typeof window !== "undefined") {
      window.addEventListener("load", () => {
        this.initSdk();
      });
      return;
    }
  }

  componentDidUpdate() {
    this.storeBlock();
  }

  isExperience = () => {
    return this.props.contest.appType === "experience";
  };

  render() {
    if (!this.props.store) {
      return null;
    }

    const noFrontMatter = this.props.block.tags?.includes("display-hints/no-front-matter");
    const frontMatter = this.props.block.tags?.includes("display-hints/front-matter");
    const hasPromotions = hasPage(this.props.store.site, "promotions");
    const listPage =
      reverseRoute(this.props.store.site, "promotions") || reverseRoute(this.props.store.site, "contests") || "";
    const className = classnames(
      ["content-layout-container", "contest-detail"],
      getThemingCssFromTags(this.props.block.tags),
    );

    return (
      <Translation>
        {t => {
          const eyebrowText = hasPromotions
            ? abTranslator("promotions", this.props.store?.testGroups, t)
            : t("contests");
          if (frontMatter) {
            return (
              <ContestHeader
                frontMatter={true}
                eyebrowUrl={listPage}
                eyebrowText={eyebrowText}
                title={this.props.contest.title}
              />
            );
          }

          return (
            <Container className={className}>
              {noFrontMatter ? null : (
                <ContestHeader eyebrowUrl={listPage} eyebrowText={eyebrowText} title={this.props.contest.title} />
              )}
              <ShareButtons
                url={this.props.contest.pageUrl}
                title={this.props.contest.title}
                desc={this.props.contest.description}
                className="social-top"
              />
              <div className="embed-responsive-item" ref={this.contentRef}>
                {this.isExperience() ? (
                  <>
                    {/* Audience 2.0 Loader Animation */}
                    <style>
                      {`@keyframes audience-${this.props.contest.appId}-loader-hide {
                        0%{visibility:hidden}
                        99%{visibility:hidden}
                        100%{visibility:visible}
                      }`}
                    </style>
                    {/* Loader with fallback link */}
                    <div
                      id={`audience-${this.props.contest.appId}-loader`}
                      style={{ animation: `audience-${this.props.contest.appId}-loader-hide 5s` }}
                    >
                      <a
                        style={{
                          display: "block",
                          maxWidth: "fit-content",
                          marginLeft: "auto",
                          marginRight: "auto",
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://xp.audience.io/experience/${this.props.contest.appId}`}
                      >
                        Experience not loading? Click here
                      </a>
                    </div>
                    {/* Audience 2.0 container */}
                    <div
                      className="audience-container"
                      data-load-enabled="true"
                      data-load-behavior="embed"
                      data-load-revisit-enabled="true"
                      data-load-revisit-behavior="embed"
                      data-id={this.props.contest.appId}
                      data-type="experience"
                    />
                    <script async defer type="text/javascript" src={this.sdkHost} />
                  </>
                ) : (
                  <>
                    {/* Audience 1.0 container */}
                    <div className="aptivada-campaign" />
                    <div className="component-embed-script" data-src={`https://${this.sdkHost}/sdk.js`} />
                  </>
                )}
              </div>
            </Container>
          );
        }}
      </Translation>
    );
  }
}

export default Contest;
